import {
  useContext,
  useEffect,
  useState,
  lazy,
  Suspense,
  type ComponentType,
  type ReactNode,
} from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { Routes } from '@/utils/routes';
import { Home } from './Home';
import {
  CanAccessBills,
  CanAccessContainerStation,
  CanAccessItemLocation,
  CanAccessEventOverview,
  CanAccessOperationsData,
  CanAccessPickingJobs,
  CanAccessPartnerData,
  CanAccessUserData,
  HasPartnerDataClaim,
  IsAdmin,
  IsEventAccountUser,
  IsEventCleaningEmployeeClaim,
  IsEventOwnerClaim,
  IsFinance,
  IsMarketing,
  IsVytalEmployee,
  Viewer,
} from '../providers/ViewerContextProvider';
import { Users } from '../views/Users';
import Forbidden from './Forbidden';
import PartnerMerchantStorePage from '../views/MerchantComponents/PartnerMerchantStorePage';
import CheckoutsByUserPage from '../views/MerchantComponents/CheckoutsByUserPage';
import SimulateWebhooksPage from '../views/MerchantComponents/SimulateWebhooksPage';
import { ContainerView } from '../views/ContainerView';
import EditConfigPage from '../views/Config/EditConfigPage';
import EditMerchantOrderConfigPage from '../views/Config/EditMerchantOrderConfigPage';
import CarrierCountryConfigPage from '../views/Config/CarrierCountryConfigPage';
import WarehouseSettingsPage from '../views/Config/WarehouseSettingsPage';
import EditContainerPrices from '../views/ContainerTypes/EditContainerPrices';
import EditContainerInfo from '../views/ContainerTypes/EditContainerInfo';
import EditContainerInfoNew from '../views/ContainerTypes/EditContainerInfoNew';
import EditRegionPage from '../views/Region/EditRegionPage';
import { BillingView } from '../views/BillingView';
import InventoryCheckView from '../views/InventoryCheckView';
import SelfSignupConfigListView from '../views/PartnerSelfSignup/PartnerSelfSignupListView';
import SelfSignupConfigDetailView from '../views/PartnerSelfSignup/PartnerSelfSignupDetailView';
import PickpackRedirect from '@/utils/pickpackRedirect';
import LegacyMerchantLinkRedirect from '@/utils/LegacyMerchantLinkRedirect';
import SalesforceRedirect from '@/utils/salesforceRedirect';
import FinancePage from '../views/Finance/FinancePage';
import AccountLedgerCsvUpload from '../views/Finance/AccountLedgerCsvUpload';
import FinanceBulkImportPage from '../views/Finance/BulkImport/FinanceBulkImportPage';
import { EditMerchantOrder } from './EditMerchantOrder/EditMerchantOrder';
import { InventoryCheckListView } from '../views/InventoryCheckListView';
import { EditMerchantOrderItemOptionView } from '../views/EditMerchantOrderItemOptionView';
import { VendingMachineView } from '../views/VendingMachineView';
import EditGrafanaQueryConfigPage from '../views/Config/EditGrafanaQueryConfig';
import { EditRiderAreaConfigPage } from '../views/Config/EditRiderAreaConfigPage';
import AddContainerCollectionsView from '../views/ContainerCollection/AddContainerCollections';
import ContainerCollectionByLocationView from '../views/ContainerCollection/ContainerCollectionLocationsView';
import ContainerCollectionView from '../views/ContainerCollection/ContainerCollectionView';
import AdminOrder from '../pages/AdminOrder';
import AdminPartnerSelfSignup from '../pages/AdminPartnerSelfSignup';
import StoreDetail from '../pages/AdminStoreDetail';
import Loading from './../components/Loading';
import { EditCustomBillomatArticles } from '../pages/EditCustomBillomatArticles';
import { AppTranslationsPage } from '../views/AppTranslations/AppTranslationsPage';
import { DesignPage } from '@/pages/Design/DesignPage';
import PartnerSearchNew from '../pages/PartnerSearchNew';
import LegacyBrandSearch from '../pages/LegacyBrandSearch';
import CreateCheckouts from '../pages/CreateCheckouts';
import { InvoiceCollection } from '../pages/InvoiceCollection/InvoiceCollection';
import { InvoiceCollectionList } from '../pages/InvoiceCollection/InvoiceCollectionsList';
import { ContainerStationDetailView } from '../views/ContainerStationDetailView';
import ContainerStationSearchView from '../views/ContainerStationSearchView';
import ContainerTypesView from '../views/ContainerTypesView';
import { EventsIndexView } from '../views/Events/EventsIndex';
import { EventsAccountsIndexView } from '../views/Events/EventAccountsIndex';
import ItemLocationSearchView from '../views/ItemLocationSearchView';
import ItemLocationDetailView from '../views/ItemLocation/ItemLocationDetailView';
import { EditEventPage } from '../views/Events/Events/EditEventPage';
import { EventDetailPage } from '../views/Events/Events/EventDetailPage';
import { DealQuotePage } from '../views/DealQuote/DealQuotePage';
import { CreateEventAccountPage } from '../views/Events/EventAccounts/CreateEventAccountsPage';
import { EventAccountDetailPage } from '../views/Events/EventAccounts/EventAccountDetailPage';
import { EventAccountEditPage } from '../views/Events/EventAccounts/EventAccountEditPage';
import { CreateEventPage } from '../views/Events/Events/CreateEventPage';
import { DownloadReportPage } from '../views/Finance/DownloadReportPage';
import { AuthorizedSignerDetailPage } from '../views/Events/AuthorizedSigners/AuthorizedSignerDetailPage';
import { OrderFulfillmentDetailPage } from '../views/Events/OrderFulfillment/OrderFulfillmentDetailPage';
import OrderFulfillmentSearchPage from '../views/Events/OrderFulfillment/OrderFulfillmentSearchPage';
import { ScanTunnelsIndexView } from '../views/Events/ScanTunnelsIndex';
import { CreateScanTunnelPage } from '../views/Events/ScanTunnels/CreateScanTunnelPage';
import { ScanTunnelEditPage } from '../views/Events/ScanTunnels/ScanTunnelEditPage';
import { ScanTunnelDetailPage } from '../views/Events/ScanTunnels/ScanTunnelDetailPage';
import { EventAccountHomePage } from '../views/Events/EventsDashboard/EventAccountHome';
import { ScanTunnelSessionDetailPage } from '../views/Events/ScanTunnels/ScanTunnelSessionDetailPage';
import { ThemeContext } from '../providers/ThemeContextProvider';
import { EventInventoryPage } from '../views/Events/EventsDashboard/subpages/InventoryPage';
import { EventAccountFulfillmentsPage } from '../views/Events/EventsDashboard/subpages/EventAccountFulfillmentsPage';
import { EventSettingsPage } from '../views/Events/EventsDashboard/subpages/SettingsPage';
import { DepositClearingPage } from '../views/Events/EventAccounts/DepositClearingPage';
import { CreateOrderFulfillmentPage } from '../views/Events/OrderFulfillment/CreateOrderFulfillmentPage';
import ContainerQrRedirectionCampaignView from '../views/ContainerQrRedirectionCampaignView/ContainerQrRedirectionCampaignView';
import UnlinkedOrderFulfillmentPictures from '../views/Events/OrderFulfillment/UnlinkedOrderFulfillmentPictures';
import EventOverviewPage from '../views/Events/EventOverview/EventOverviewPage';
import EditContainerTypeGroups from '../views/ContainerTypes/EditContainerTypeGroups';
import EditContainerTypeGroupsBeta from '../views/ContainerTypes/EditContainerTypeGroupsBeta';
import NotFound from './NotFound';
import ZohoRedirect from '../ZohoRedirect';
import { hasClickHouse, isDev, isLocalDev } from '@/utils/isDev';
import EditItemTypes from '../views/ItemType/EditItemTypes';
import ServicePackagesRouter from '../views/ServicePackagePlans/ServicePackagesRouter';
import EventOwnerDashboard from '../views/Events/EventOwnerDashboard/EventOwnerDashboard';
import { TrackableItemDetailPage } from '../views/TrackableItems/TrackableItemDetailPage';
import { Role } from '../contants';
import { BootstrapRoute } from '@/utils/BootstrapRoute';
import { PrimeReactContext } from 'primereact/api';
import PublicEventOrderForm from '@/views/Events/EventOrder/PublicEventOrderForm/PublicEventOrderForm';
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar';
import { AppSidebar } from './AppSidebar/AppSidebar';
import { useSidebarLayout } from './AppSidebar/useSidebarLayout';
import { useIsPublicPage } from '@/utils/publicPage';
import { SidebarContextProvider } from './SidebarContext';
import { SidebarUserMenu } from './SidebarUserMenu';
import { useIsMobile } from '@/hooks/use-mobile';
import { LanguageContext } from '@/providers/LanguageContextProvider';
import MerchantEditPage from '@/pages/MerchantEdit/MerchantEditPage';
import PartnerAppRedirect from '@/pages/PartnerAppRedirect/PartnerAppRedirect';
import {
  BreadcrumbsOutlet,
  BreadcrumbsProvider,
} from '@/components/Breadcrumbs/BreadcrumbsOutlet';
import { OrdersPage } from '@/views/Order';
import { PickingJobsPage } from '@/views/PickingJobs';
import {
  MonthlyInvoicingOverviewPage,
  MonthlyInvoicingRunDetailPage,
} from '@/pages/MonthlyInvoicing';

const AssetSearchView = lazy(() => import('../views/AssetSearchView'));
const StadiumStockPage = lazy(
  () => import('../pages/StadiumStock/StadiumStockPage'),
);
const ReturnStationPage = lazy(
  () => import('../pages/ReturnStation/ReturnStationPage'),
);
const WashingHubPage = lazy(() => import('../pages/WashingHub/WashingHubPage'));
const StoreListView = lazy(
  () => import('./../views/StoreListView/StoreListView'),
);
const AssetsView = lazy(
  () => import('./../views/AssetsListView/AssetsListView'),
);
const CampaignListView = lazy(
  () => import('./../views/AdminCampaignList/index.jsx'),
);
const CampaignView = lazy(() => import('./../views/AdminCampaign/index.jsx'));
const NewsContentView = lazy(() => import('./../views/AdminNews/index.jsx'));
const DevToolsModal = lazy(() => import('./DevToolsModal'));
const IntegrationApiTools = lazy(() => import('../pages/IntegrationApiTools'));
const DevSeeding = lazy(() => import('../pages/DevSeeding'));
const AnalyticsUsersPage = lazy(
  () => import('../views/AnalyticsUsers/AnalyticsUsersPage'),
);
const OpsWarningsPage = lazy(
  () => import('../views/OpsWarnings/OpsWarningsPage'),
);
const MiniReturnersPage = lazy(
  () => import('../views/MiniReturners/MiniReturnersPage'),
);
const SweepstakesManagement = lazy(
  () => import('../views/SweepstakesManagement/SweepstakesManagementPage'),
);

const Loadable =
  <P extends object>(Component: ComponentType<P>) =>
  (props: P) => (
    <Suspense fallback={<Loading />}>
      <Component {...props} />
    </Suspense>
  );

const LoadableAssetSearchView = Loadable(AssetSearchView);
const LoadableStoreListView = Loadable(StoreListView);
const LoadableAssetsView = Loadable(AssetsView);
const LoadableCampaignListView = Loadable(CampaignListView);
const LoadableCampaignView = Loadable(CampaignView);
const LoadableNewsContentView = Loadable(NewsContentView);
const DevToolsModalView = Loadable(DevToolsModal);
const IntegrationApiToolsView = Loadable(IntegrationApiTools);
const DevSeedingView = Loadable(DevSeeding);
const LoadableAnalyticsUsersPage = Loadable(AnalyticsUsersPage);
const LoadableOpsWarningsPage = Loadable(OpsWarningsPage);
const LoadableMiniReturnersPage = Loadable(MiniReturnersPage);
const SweepstakesManagementView = Loadable(SweepstakesManagement);

function AppShellHeader({ viewer }: { viewer: Viewer }) {
  const { isInteractive, body } = useSidebarLayout(viewer);

  if (!isInteractive) {
    return null;
  }

  return (
    <header>
      <div className="tw:h-sidebar-header-height tw:flex tw:items-center tw:gap-2">
        {/* When the sidebar is an empty icon rail there is nothing to expand,
            so the trigger is dropped. */}
        {body !== 'empty' && <SidebarTrigger className="tw:-ml-1" />}
        <BreadcrumbsOutlet />
      </div>
    </header>
  );
}

/**
 * Wraps SidebarProvider so the minimal condition (which depends on the per-page
 * context sidebar) can be read from SidebarContext: when minimal, the provider
 * is forced collapsed so the sidebar sits in its icon-rail form. Must render
 * inside SidebarContextProvider.
 */
function SidebarShell({
  viewer,
  children,
}: {
  viewer: Viewer;
  children: ReactNode;
}) {
  const { body } = useSidebarLayout(viewer);
  const isEmpty = body === 'empty';

  return (
    <SidebarProvider
      className="tw:flex-1 tw:min-h-0"
      open={isEmpty ? false : undefined}
      // No body to expand on the icon rail; keep Cmd+B from mutating the
      // persisted open state behind an invisible sidebar.
      shortcutEnabled={!isEmpty}
    >
      {children}
    </SidebarProvider>
  );
}

/**
 * On mobile public pages the sidebar collapses into an unopenable drawer, so the
 * user menu lives here instead: a top header matching the sidebar's background
 * and border, with the Vytal logo on the left and the menu trigger on the right.
 */
function PublicMobileHeader({
  viewer,
  onOpenDevTools,
}: {
  viewer: Viewer;
  onOpenDevTools?: () => void;
}) {
  return (
    <header className="tw:flex tw:h-16 tw:shrink-0 tw:items-center tw:justify-between tw:border-b tw:border-sidebar-border tw:bg-sidebar tw:px-4">
      <img
        className="tw:h-[28px] tw:object-contain tw:dark:hidden"
        src="/vytal_logo.png"
      />
      <img
        className="tw:hidden tw:h-[28px] tw:object-contain tw:dark:block"
        src="/logo_white.svg"
      />
      <SidebarUserMenu
        viewer={viewer}
        onOpenDevTools={onOpenDevTools}
        variant="compact"
      />
    </header>
  );
}

export default function AppShellWrapper({ viewer }: { viewer: Viewer }) {
  const { setPt } = useContext(PrimeReactContext);
  const { colorMode } = useContext(ThemeContext);
  const { locale } = useContext(LanguageContext);

  // Read useIsPublicPage directly instead of useSidebarLayout: this runs
  // outside SidebarContextProvider (mounted below), so it can't use the context.
  const isInteractiveShell = !useIsPublicPage();
  const isMobile = useIsMobile();
  // Mobile public pages get a top header instead of the rail (which becomes an
  // unopenable drawer on mobile).
  const showPublicMobileHeader = !isInteractiveShell && isMobile;

  const [isDevToolsModalOpen, setIsDevToolsModalOpen] = useState(false);

  const isDevelopmentEnv = isDev() || isLocalDev();

  useEffect(() => {
    const body = document.querySelector('body');

    setPt({
      dialog: {
        root: {
          // @ts-expect-error - It's a custom attribute
          'data-use-bs': true,
        },
      },
      tooltip: {
        root: {
          // @ts-expect-error - It's a custom attribute
          'data-use-bs': true,
        },
      },
    });

    if (body) {
      body.setAttribute('data-bs-theme', colorMode);
      body.setAttribute('class', `bs-theme-${colorMode}`);
    }
  }, [colorMode, setPt]);

  return (
    <BreadcrumbsProvider>
      <div
        key={locale}
        className="tw:flex tw:flex-col tw:h-screen tw:overflow-hidden"
      >
        {showPublicMobileHeader && (
          <PublicMobileHeader
            viewer={viewer}
            onOpenDevTools={
              isDevelopmentEnv ? () => setIsDevToolsModalOpen(true) : undefined
            }
          />
        )}
        <SidebarContextProvider>
          <SidebarShell viewer={viewer}>
            <AppSidebar
              viewer={viewer}
              onOpenDevTools={
                isDevelopmentEnv
                  ? () => setIsDevToolsModalOpen(true)
                  : undefined
              }
            />
            <SidebarInset className="tw:flex tw:flex-1 tw:flex-col tw:min-h-0 tw:overflow-auto tw:bg-background tw:p-6 tw:pt-0 tw:pb-8">
              <AppShellHeader viewer={viewer} />
              <main className="tw:flex-1">
                {isDevelopmentEnv && (
                  <DevToolsModalView
                    isOpen={isDevToolsModalOpen}
                    toggle={() => setIsDevToolsModalOpen(!isDevToolsModalOpen)}
                  />
                )}
                <div id="modal-root" />
                <BootstrapRoute exact path={Routes.home()} component={Home} />
                <BootstrapRoute
                  path={Routes.users(':userId?')}
                  component={
                    CanAccessUserData(viewer) ||
                    viewer.role === Role.humanResources ||
                    CanAccessPartnerData(viewer)
                      ? Users
                      : Forbidden
                  }
                />
                <Switch>
                  {/* Legacy brand editing moved out of /partner into /legacy. */}
                  <BootstrapRoute
                    path={Routes.legacyBrandEdit(':brandId')}
                    component={
                      CanAccessPartnerData(viewer)
                        ? MerchantEditPage
                        : Forbidden
                    }
                  />
                  <BootstrapRoute
                    path={Routes.legacyBrandSearch()}
                    component={
                      CanAccessPartnerData(viewer)
                        ? LegacyBrandSearch
                        : Forbidden
                    }
                  />
                  {/* Deprecated: keep old /partner/brand-edit path working. */}
                  <BootstrapRoute
                    path={Routes.partnerBrandEdit(':brandId')}
                    component={
                      CanAccessPartnerData(viewer)
                        ? MerchantEditPage
                        : Forbidden
                    }
                  />
                  {/* New searchAdminV2-based partner hierarchy routes. */}
                  <Route
                    path={Routes.partnerAccount(':accountId?')}
                    component={
                      CanAccessPartnerData(viewer)
                        ? PartnerSearchNew
                        : Forbidden
                    }
                  />
                  <Route
                    path={Routes.partnerEvent(':eventId?')}
                    component={
                      CanAccessPartnerData(viewer)
                        ? PartnerSearchNew
                        : Forbidden
                    }
                  />
                  {/*
                    Unguarded like /partner/merchants: partner staff have no
                    frontend "partner" role (CanAccessPartnerData is internal
                    roles only), so a role guard would wrongly block the main
                    audience. Access is enforced by the backend endpoint
                    (GetCurrentHoldingsForUser, roster-scoped).
                  */}
                  <Route
                    path="/partner/user-checkouts"
                    component={CheckoutsByUserPage}
                  />
                  {/*
                    Unguarded for the same reason as /partner/user-checkouts:
                    partner staff are the audience and the backend endpoints
                    (GetTestableCheckouts/SimulateWebhook) enforce store links
                    and the dev-only gate.
                  */}
                  <Route
                    path="/partner/integration-webhooks"
                    component={SimulateWebhooksPage}
                  />
                  {/*
                    Unguarded and hidden (no sidebar entry): the stadium stock plan
                    is reachable by URL only, and colugo's getStoresContainerStock
                    enforces partner data access for the stock it shows.
                  */}
                  <Route
                    path={Routes.stadiumStock()}
                    component={StadiumStockPage}
                  />
                  {/*
                    Unguarded and hidden like the stadium plan: the return
                    station kiosk for an iPad at a fair. Its container lookup
                    (CheckCode) only needs the logged-in session.
                  */}
                  <Route
                    path={Routes.returnStation()}
                    component={ReturnStationPage}
                  />
                  {/*
                    Unguarded and hidden like the stadium plan: the washing hub
                    TV. colugo's washingHub operations are limited to
                    operations and administrators, and the page says so when
                    the logged-in account is neither.
                  */}
                  <Route
                    path={Routes.washingHub()}
                    component={WashingHubPage}
                  />
                  <Route
                    path={Routes.partnerStore(':storeId?')}
                    component={
                      CanAccessPartnerData(viewer)
                        ? PartnerSearchNew
                        : Forbidden
                    }
                  />
                  {/* Deprecated: keep old /partner/brand/... URLs resolvable. */}
                  <Route
                    path={Routes.partnerBrandStore(':brandId?', ':storeId?')}
                    component={
                      CanAccessPartnerData(viewer)
                        ? PartnerSearchNew
                        : Forbidden
                    }
                  />
                  <Route
                    path={Routes.partnerBrand(':brandId?')}
                    component={
                      CanAccessPartnerData(viewer)
                        ? PartnerSearchNew
                        : Forbidden
                    }
                  />
                  <BootstrapRoute
                    path={Routes.partnerMerchants()}
                    component={PartnerMerchantStorePage}
                  />
                  <BootstrapRoute
                    path={Routes.partner()}
                    component={
                      CanAccessPartnerData(viewer)
                        ? PartnerSearchNew
                        : Forbidden
                    }
                  />
                </Switch>
                <BootstrapRoute
                  path={Routes.adminOrder(':orderId')}
                  component={AdminOrder}
                />
                <BootstrapRoute
                  path={Routes.partnerSelfSignup(':partnerSelfSignupId')}
                  component={
                    CanAccessPartnerData(viewer)
                      ? AdminPartnerSelfSignup
                      : Forbidden
                  }
                />

                <BootstrapRoute
                  path={Routes.storeDetail(':storeId')}
                  component={StoreDetail}
                />
                <BootstrapRoute
                  path={Routes.containers()}
                  component={
                    CanAccessPartnerData(viewer) ? ContainerView : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.container(':containerId?')}
                  component={
                    CanAccessPartnerData(viewer) ? ContainerView : Forbidden
                  }
                />
                <Switch>
                  <BootstrapRoute
                    path={Routes.events()}
                    component={
                      CanAccessPartnerData(viewer) ? EventsIndexView : Forbidden
                    }
                  />
                  <Route
                    path={Routes.eventOverview()}
                    component={
                      CanAccessEventOverview(viewer)
                        ? EventOverviewPage
                        : Forbidden
                    }
                  />
                  <BootstrapRoute
                    path={Routes.createEvent()}
                    component={CreateEventPage}
                  />
                  <BootstrapRoute
                    path={Routes.editEvent(':eventId')}
                    component={
                      CanAccessPartnerData(viewer) ? EditEventPage : Forbidden
                    }
                  />
                  <Route
                    path={Routes.eventDetail(':eventId')}
                    component={
                      CanAccessPartnerData(viewer) ? EventDetailPage : Forbidden
                    }
                  />
                  <Route
                    path={Routes.dealQuote(':zohoDealId')}
                    component={
                      CanAccessPartnerData(viewer) ? DealQuotePage : Forbidden
                    }
                  />
                  <BootstrapRoute
                    path={Routes.eventAccounts()}
                    component={
                      CanAccessPartnerData(viewer)
                        ? EventsAccountsIndexView
                        : Forbidden
                    }
                  />
                  <BootstrapRoute
                    path={Routes.createEventAccount()}
                    component={
                      CanAccessPartnerData(viewer)
                        ? CreateEventAccountPage
                        : Forbidden
                    }
                  />
                  <BootstrapRoute
                    path={Routes.editEventAccount(':eventAccountId')}
                    component={
                      CanAccessPartnerData(viewer)
                        ? EventAccountEditPage
                        : Forbidden
                    }
                  />
                  <BootstrapRoute
                    path={Routes.eventAccountDetail(':eventAccountId')}
                    component={
                      CanAccessPartnerData(viewer)
                        ? EventAccountDetailPage
                        : Forbidden
                    }
                  />
                  <BootstrapRoute
                    path={Routes.partnerAccountDetail(':partnerAccountId')}
                    component={
                      CanAccessPartnerData(viewer)
                        ? EventAccountDetailPage
                        : Forbidden
                    }
                  />
                  <BootstrapRoute
                    path={Routes.eventAccountDepositClearing(
                      ':eventId',
                      ':eventAccountId',
                    )}
                    component={
                      CanAccessPartnerData(viewer)
                        ? DepositClearingPage
                        : Forbidden
                    }
                  />
                </Switch>
                <Switch>
                  <BootstrapRoute
                    path={Routes.selectedNews(':newsContentId')}
                    component={
                      IsMarketing(viewer) ? LoadableNewsContentView : Forbidden
                    }
                  />
                  <BootstrapRoute
                    path={Routes.news()}
                    component={
                      IsMarketing(viewer) ? LoadableNewsContentView : Forbidden
                    }
                  />
                </Switch>
                <BootstrapRoute
                  path={Routes.campaigns()}
                  component={
                    IsMarketing(viewer) ? LoadableCampaignListView : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.campaign(':campaignId')}
                  component={
                    IsMarketing(viewer) ? LoadableCampaignView : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.sweepstakesManagementCampaign(':campaignId')}
                  component={
                    IsMarketing(viewer) ? SweepstakesManagementView : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.sweepstakesManagement()}
                  component={
                    IsMarketing(viewer) ? SweepstakesManagementView : Forbidden
                  }
                  exact
                />
                <BootstrapRoute
                  path={Routes.invoiceCollection(':id')}
                  component={
                    CanAccessPartnerData(viewer) ? InvoiceCollection : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.invoiceCollections()}
                  component={
                    CanAccessPartnerData(viewer)
                      ? InvoiceCollectionList
                      : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.carrierCountryConfig()}
                  component={
                    viewer.role === Role.administrator
                      ? CarrierCountryConfigPage
                      : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.warehouseSettings()}
                  component={
                    viewer.role === Role.administrator
                      ? WarehouseSettingsPage
                      : Forbidden
                  }
                />
                <BootstrapRoute
                  exact
                  path={Routes.config()}
                  component={
                    viewer.role === Role.administrator
                      ? EditConfigPage
                      : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.merchantOrderConfig()}
                  component={
                    viewer.role === Role.administrator
                      ? EditMerchantOrderConfigPage
                      : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.editContainerPrices()}
                  component={
                    viewer.role === Role.administrator
                      ? EditContainerPrices
                      : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.editContainerInfo()}
                  component={EditContainerInfo}
                />
                {/*
                  Container Types reads are gated server-side with
                  mustBeInternal (colugo). IsVytalEmployee mirrors that exact
                  role set, so non-internal users get Forbidden instead of
                  triggering handled 403s from the page's on-mount admin
                  queries. See Sentry COLUGO-BACKEND-2V4 / 2V5.
                */}
                <Route
                  path={Routes.editContainerTypes()}
                  component={
                    IsVytalEmployee(viewer) ? EditContainerInfoNew : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.editItemTypes()}
                  component={EditItemTypes}
                />
                <BootstrapRoute
                  path={Routes.servicePackages()}
                  component={ServicePackagesRouter}
                />
                <BootstrapRoute
                  path={Routes.editContainerTypeGroups()}
                  component={
                    viewer.role === Role.administrator
                      ? EditContainerTypeGroups
                      : Forbidden
                  }
                />
                <Route
                  path={Routes.editContainerTypeGroupsBeta()}
                  component={
                    viewer.role === Role.administrator
                      ? EditContainerTypeGroupsBeta
                      : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.stores()}
                  component={
                    CanAccessPartnerData(viewer)
                      ? LoadableStoreListView
                      : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.storeAssets()}
                  component={
                    CanAccessPartnerData(viewer)
                      ? LoadableAssetsView
                      : Forbidden
                  }
                />

                <BootstrapRoute
                  path={Routes.trackableItem(':itemId')}
                  component={
                    CanAccessPartnerData(viewer)
                      ? TrackableItemDetailPage
                      : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.regions()}
                  component={
                    viewer.role === Role.administrator
                      ? EditRegionPage
                      : Forbidden
                  }
                />
                <BootstrapRoute
                  path={[
                    Routes.billing(':page?', ':showPaid?'),
                    Routes.bill(':type', ':billId'),
                  ]}
                  component={CanAccessBills(viewer) ? BillingView : Forbidden}
                />
                <BootstrapRoute
                  path={Routes.inventoryCheck(':id')}
                  component={InventoryCheckView}
                />
                <BootstrapRoute
                  path={Routes.selfSignupConfigs()}
                  component={
                    viewer.role === Role.administrator
                      ? SelfSignupConfigListView
                      : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.selfSignupConfig(':configId')}
                  component={
                    viewer.role === Role.administrator
                      ? SelfSignupConfigDetailView
                      : Forbidden
                  }
                />

                <BootstrapRoute
                  path={Routes.pickpackStores(':storeId')}
                  component={PickpackRedirect}
                />
                <BootstrapRoute
                  path={Routes.pickpackBrands(':brandId')}
                  component={PickpackRedirect}
                />
                <BootstrapRoute
                  path={Routes.merchants(':merchantId?', ':storeId?')}
                  component={LegacyMerchantLinkRedirect}
                />
                <BootstrapRoute
                  path={Routes.salesforceStores(':storeId')}
                  component={SalesforceRedirect}
                />
                <BootstrapRoute
                  path={Routes.merchants(':merchantId?')}
                  component={LegacyMerchantLinkRedirect}
                  exact
                />
                <Switch>
                  <BootstrapRoute
                    path={Routes.financeReportsDownload()}
                    component={
                      IsFinance(viewer) ? DownloadReportPage : Forbidden
                    }
                  />
                  <BootstrapRoute
                    path={Routes.accountLedgerCsvUpload()}
                    component={
                      IsFinance(viewer) ? AccountLedgerCsvUpload : Forbidden
                    }
                  />
                  <Route
                    path={Routes.financeBulkImport()}
                    component={
                      IsFinance(viewer) ? FinanceBulkImportPage : Forbidden
                    }
                  />
                  <BootstrapRoute
                    path={Routes.finance()}
                    component={IsFinance(viewer) ? FinancePage : Forbidden}
                  />
                </Switch>
                <Switch>
                  <Route
                    path={Routes.monthlyInvoicingRun(':id')}
                    component={
                      IsFinance(viewer)
                        ? MonthlyInvoicingRunDetailPage
                        : Forbidden
                    }
                  />
                  <Route
                    path={Routes.monthlyInvoicing()}
                    component={
                      IsFinance(viewer)
                        ? MonthlyInvoicingOverviewPage
                        : Forbidden
                    }
                  />
                </Switch>
                <BootstrapRoute
                  path={Routes.appTranslations()}
                  component={
                    IsVytalEmployee(viewer) ? AppTranslationsPage : Forbidden
                  }
                />
                <Route
                  path={Routes.design()}
                  component={IsVytalEmployee(viewer) ? DesignPage : Forbidden}
                />

                <Route
                  path={Routes.orders()}
                  component={
                    CanAccessPartnerData(viewer) ? OrdersPage : Forbidden
                  }
                />
                <Route
                  path={Routes.pickingJobs()}
                  component={
                    CanAccessPickingJobs(viewer) ? PickingJobsPage : Forbidden
                  }
                />
                {/* Old order-management URLs: redirect bookmarks to the new
                    order pages, which replaced them entirely. */}
                <Route
                  exact
                  path={Routes.legacyMerchantOrders()}
                  render={({ location }) => (
                    <Redirect to={{ ...location, pathname: Routes.orders() }} />
                  )}
                />
                <Route
                  path={Routes.legacyMerchantOrder(':orderId')}
                  render={({ location, match }) => (
                    <Redirect
                      to={{
                        ...location,
                        pathname: Routes.orderDetails(match.params.orderId),
                      }}
                    />
                  )}
                />
                <BootstrapRoute
                  path={Routes.inventoryChecks()}
                  component={
                    CanAccessPartnerData(viewer)
                      ? InventoryCheckListView
                      : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.createOrder(':vytalId')}
                  component={EditMerchantOrder}
                />
                <BootstrapRoute
                  path={Routes.editMerchantOrderItemOptions()}
                  component={
                    viewer.role === Role.administrator
                      ? EditMerchantOrderItemOptionView
                      : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.vendingMachine(
                    ':pickpackId',
                    ':vendingMachineId',
                  )}
                  component={
                    CanAccessPartnerData(viewer)
                      ? VendingMachineView
                      : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.grafanaQueries()}
                  component={
                    viewer.role === Role.administrator
                      ? EditGrafanaQueryConfigPage
                      : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.riderArea()}
                  component={
                    viewer.role === Role.administrator
                      ? EditRiderAreaConfigPage
                      : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.addContainerCollections()}
                  component={AddContainerCollectionsView}
                />
                <BootstrapRoute
                  path={Routes.containerCollectionByLocation()}
                  component={
                    CanAccessPartnerData(viewer)
                      ? ContainerCollectionByLocationView
                      : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.containerCollection(':id')}
                  component={
                    CanAccessPartnerData(viewer)
                      ? ContainerCollectionView
                      : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.containerStationSearch()}
                  component={
                    CanAccessPartnerData(viewer) ||
                    IsEventCleaningEmployeeClaim(viewer)
                      ? ContainerStationSearchView
                      : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.containerTypes()}
                  component={
                    CanAccessPartnerData(viewer) ||
                    IsEventCleaningEmployeeClaim(viewer)
                      ? ContainerTypesView
                      : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.eventAssetSearch()}
                  component={
                    CanAccessPartnerData(viewer) ||
                    IsEventCleaningEmployeeClaim(viewer)
                      ? LoadableAssetSearchView
                      : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.containerQrRedirect()}
                  component={
                    IsVytalEmployee(viewer)
                      ? ContainerQrRedirectionCampaignView
                      : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.orderFulfillmentSearch()}
                  component={
                    CanAccessPartnerData(viewer)
                      ? OrderFulfillmentSearchPage
                      : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.itemLocationSearch()}
                  component={
                    CanAccessPartnerData(viewer) ||
                    IsEventCleaningEmployeeClaim(viewer)
                      ? ItemLocationSearchView
                      : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.authorizedSigner(':authorizedSignerId')}
                  component={AuthorizedSignerDetailPage}
                />
                <BootstrapRoute
                  path={Routes.orderFulfillment(':fulfillmentId')}
                  component={
                    CanAccessPartnerData(viewer) || IsEventAccountUser(viewer)
                      ? OrderFulfillmentDetailPage
                      : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.createCheckouts()}
                  component={IsAdmin(viewer) ? CreateCheckouts : Forbidden}
                />
                {isDevelopmentEnv && (
                  <>
                    <BootstrapRoute
                      path={Routes.integrationApiTools()}
                      component={
                        IsAdmin(viewer) ? IntegrationApiToolsView : Forbidden
                      }
                    />
                    <BootstrapRoute
                      path={Routes.devSeeding()}
                      component={IsAdmin(viewer) ? DevSeedingView : Forbidden}
                    />
                  </>
                )}
                {/* Analytics Users route is only available in production since we don't have a separate dev environment for this feature */}
                {!isDevelopmentEnv && (
                  <BootstrapRoute
                    path={Routes.analyticsUsers()}
                    component={
                      HasPartnerDataClaim(viewer)
                        ? LoadableAnalyticsUsersPage
                        : Forbidden
                    }
                  />
                )}
                <BootstrapRoute
                  path={Routes.miniReturners()}
                  component={
                    HasPartnerDataClaim(viewer)
                      ? LoadableMiniReturnersPage
                      : Forbidden
                  }
                />
                {hasClickHouse() && (
                  <BootstrapRoute
                    path={Routes.opsWarnings()}
                    component={
                      CanAccessOperationsData(viewer)
                        ? LoadableOpsWarningsPage
                        : Forbidden
                    }
                  />
                )}
                <BootstrapRoute
                  path={Routes.editCustomBillomatArticles()}
                  component={
                    IsFinance(viewer) ? EditCustomBillomatArticles : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.containerStation(':stationId')}
                  component={
                    CanAccessContainerStation(viewer)
                      ? ContainerStationDetailView
                      : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.itemLocation(':locationId')}
                  component={
                    CanAccessItemLocation(viewer)
                      ? ItemLocationDetailView
                      : Forbidden
                  }
                />
                <Switch>
                  <BootstrapRoute
                    path={Routes.scanTunnels()}
                    component={
                      CanAccessPartnerData(viewer)
                        ? ScanTunnelsIndexView
                        : Forbidden
                    }
                  />
                  <BootstrapRoute
                    path={Routes.createScanTunnel()}
                    component={
                      CanAccessPartnerData(viewer)
                        ? CreateScanTunnelPage
                        : Forbidden
                    }
                  />
                  <BootstrapRoute
                    path={Routes.editScanTunnel(':scanTunnelId')}
                    component={
                      CanAccessPartnerData(viewer)
                        ? ScanTunnelEditPage
                        : Forbidden
                    }
                  />
                  <BootstrapRoute
                    path={Routes.scanTunnel(':scanTunnelId')}
                    component={
                      CanAccessPartnerData(viewer)
                        ? ScanTunnelDetailPage
                        : Forbidden
                    }
                  />
                  <BootstrapRoute
                    path={Routes.scanTunnelSession(':scanTunnelSessionId')}
                    component={
                      CanAccessPartnerData(viewer)
                        ? ScanTunnelSessionDetailPage
                        : Forbidden
                    }
                  />
                </Switch>

                <BootstrapRoute
                  path={Routes.createOrderFulfillment(':timestamp?')}
                  component={
                    CanAccessPartnerData(viewer)
                      ? CreateOrderFulfillmentPage
                      : Forbidden
                  }
                />

                <BootstrapRoute
                  path={Routes.unlinkedOrderFulfillmentPictures()}
                  component={
                    CanAccessPartnerData(viewer)
                      ? UnlinkedOrderFulfillmentPictures
                      : Forbidden
                  }
                />

                {/* Events dashboard */}
                <BootstrapRoute
                  path={Routes.eventsDashboardHome(':eventAccountId')}
                  component={
                    IsEventAccountUser(viewer)
                      ? EventAccountHomePage
                      : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.eventsDashboardInventory(
                    ':eventAccountId',
                    ':eventId',
                  )}
                  component={
                    IsEventAccountUser(viewer) ? EventInventoryPage : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.eventsDashboardFulfillments(
                    ':eventAccountId',
                    ':eventId',
                  )}
                  component={
                    IsEventAccountUser(viewer)
                      ? EventAccountFulfillmentsPage
                      : Forbidden
                  }
                />
                <BootstrapRoute
                  path={Routes.eventsDashboardSettings(
                    ':eventAccountId',
                    ':eventId',
                  )}
                  component={
                    IsEventAccountUser(viewer) ? EventSettingsPage : Forbidden
                  }
                />

                <Switch>
                  <Route
                    path={Routes.eventOwnerDashboardFulfillments(':eventId')}
                    component={
                      IsEventOwnerClaim(viewer)
                        ? EventOwnerDashboard
                        : Forbidden
                    }
                  />
                  <Route
                    path={Routes.eventOwnerDashboard(':eventId?')}
                    component={
                      IsEventOwnerClaim(viewer)
                        ? EventOwnerDashboard
                        : Forbidden
                    }
                  />
                </Switch>

                <BootstrapRoute
                  path={Routes.eventAccount()}
                  component={ZohoRedirect}
                />
                <BootstrapRoute
                  path={Routes.event()}
                  component={ZohoRedirect}
                />
                <Route
                  path={Routes.eventOrder(':formId?')}
                  component={PublicEventOrderForm}
                />
                <Route
                  path={Routes.partnerAppSelect()}
                  component={PartnerAppRedirect}
                />
                <BootstrapRoute path={Routes.notFound()} component={NotFound} />
              </main>
            </SidebarInset>
          </SidebarShell>
        </SidebarContextProvider>
      </div>
    </BreadcrumbsProvider>
  );
}
